import { useState, type FormEvent } from 'react'
import { useSubmitContact } from '@/services/httpServices/queries/useWebsite'
import type { ApiError } from '@/types/api'

/**
 * Live contact form. Drop-in replacement for the template's static
 * `contact.php` form — posts to the backend which stores the message and
 * emails the configured recipient.
 */
export function ContactForm() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)

  const submitContact = useSubmitContact()

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  const canSubmit = Boolean(
    name.trim() && emailValid && phone.trim() && subject.trim() && message.trim().length >= 5,
  )

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setSent(false)
    if (!canSubmit || submitContact.isPending) return

    submitContact.mutate(
      {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        subject: subject.trim(),
        message: message.trim(),
      },
      {
        onSuccess: () => {
          setSent(true)
          setName('')
          setEmail('')
          setPhone('')
          setSubject('')
          setMessage('')
        },
        onError: (err) => {
          setError(
            (err as unknown as ApiError)?.message ?? 'Something went wrong. Please try again.',
          )
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="row">
        {error && (
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              <i className="fa-solid fa-circle-exclamation me-2" aria-hidden="true"></i>
              {error}
            </div>
          </div>
        )}
        {sent && (
          <div className="col-12">
            <div className="alert alert-success" role="alert">
              <i className="fa-solid fa-circle-check me-2" aria-hidden="true"></i>
              Thank you! Your message has been received. We&apos;ll get back to you shortly.
            </div>
          </div>
        )}

        <div className="col-lg-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <input
                id="contact-name"
                className="form-control"
                name="name"
                type="text"
                autoComplete="off"
                placeholder="Name Here"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <label htmlFor="contact-name">Name*</label>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <input
                id="contact-email"
                className="form-control"
                name="email"
                type="email"
                autoComplete="off"
                placeholder="Email Here"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <label htmlFor="contact-email">Email*</label>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <input
                id="contact-phone"
                className="form-control"
                name="phone"
                type="text"
                autoComplete="off"
                placeholder="Phone Number Here"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <label htmlFor="contact-phone">Phone Number*</label>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <input
                id="contact-subject"
                className="form-control"
                name="subject"
                type="text"
                autoComplete="off"
                placeholder="Subject Here"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <label htmlFor="contact-subject">Subject*</label>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="form-group">
            <div className="form-floating field-inner">
              <textarea
                id="contact-message"
                className="form-control"
                name="message"
                autoComplete="off"
                placeholder="Type Message Here"
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              ></textarea>
              <label htmlFor="contact-message">Message*</label>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="contact-btn-wapper mt-10">
            <button
              type="submit"
              className="theme-button style-1"
              data-text="Send Message"
              disabled={!canSubmit || submitContact.isPending}
            >
              <span data-text="Send Message">
                {submitContact.isPending ? 'Sending…' : 'Send Message'}
              </span>
              <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    </form>
  )
}
